/**
 * Navigation utility functions
 * Pure functions for URL construction and entity navigation
 */

/**
 * Remove blacklisted parameters that should never appear in URLs
 * @param {URLSearchParams} searchParams - URLSearchParams to filter
 * @returns {URLSearchParams} Filtered URLSearchParams without blacklisted params
 */
export const filterBlacklistedParams = (searchParams) => {
  const filtered = new URLSearchParams();
  const blacklist = ['state']; // Parameters that should never be in URLs

  for (const [key, value] of searchParams.entries()) {
    if (!blacklist.includes(key)) {
      filtered.set(key, value);
    }
  }

  return filtered;
};

/**
 * Preserve global parameters (like range) when building a new URL
 * @param {string} path - Target path (e.g., '/traces', '/agents')
 * @param {Object} newParams - New query parameters to add
 * @param {URLSearchParams} currentParams - Current URL search params
 * @param {string} selectedRange - Current time range selection
 * @returns {string} Complete URL with preserved global params
 */
export const buildUrlWithRange = (path, newParams = {}, currentParams, selectedRange) => {
  if (!path) throw new Error('buildUrlWithRange requires path');
  if (!selectedRange) throw new Error('buildUrlWithRange requires selectedRange');

  const params = new URLSearchParams();

  // Always preserve global parameters in consistent order: env, range
  const env = currentParams?.get('env');
  if (env) params.set('env', env);

  const range = currentParams?.get('range') || selectedRange;
  params.set('range', range);

  // Add new parameters
  Object.entries(newParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.set(key, value);
    }
  });

  return `${path}?${params.toString()}`;
};

/**
 * Build entity detail URL with preserved range parameter
 * @param {string} entityType - Entity type (e.g., 'Trace', 'Agent', 'Session')
 * @param {string} entityId - Entity ID
 * @param {URLSearchParams} searchParams - Current URL search params
 * @param {string} selectedRange - Current time range selection
 * @returns {string} Complete URL with query params
 */
export const buildEntityDetailUrl = (entityType, entityId, searchParams, selectedRange) => {
  if (!entityType) throw new Error('buildEntityDetailUrl requires entityType');
  if (!entityId) throw new Error('buildEntityDetailUrl requires entityId');
  if (!searchParams) throw new Error('buildEntityDetailUrl requires searchParams');
  if (!selectedRange) throw new Error('buildEntityDetailUrl requires selectedRange');

  const params = filterBlacklistedParams(new URLSearchParams(searchParams));

  // Only preserve range parameter for detail pages
  const detailParams = new URLSearchParams();
  detailParams.set('range', params.get('range') || selectedRange);

  // Preserve tab if present
  if (params.get('tab')) {
    detailParams.set('tab', params.get('tab'));
  }

  // Construct entity detail path
  const entityPath = `${entityType.toLowerCase()}s`;

  return `/${entityPath}/${encodeURIComponent(entityId)}?${detailParams.toString()}`;
};
